import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DateFormatPipe, PhoneFormatPipe } from '../../../../shared/pipes';
import { TeamReportService } from '../../../../core/services/team-report.service';
import { OrganizationContextService } from '../../../../core/services/organization-context.service';
import {
  TeamMember,
  TestResult,
  SummaryCounts,
  SafetyTestInfo,
  MemberOverallStatus,
  TestResultStatus
} from '../../../../core/models/team-report.model';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';

/**
 * TeamReportComponent
 *
 * Displays team members' safety test completion status.
 * Fetches real data from the backend team report API.
 *
 * Reference: /agent-os/specs/2026-01-15-team-report-safety-tests/spec.md
 */
@Component({
  selector: 'app-team-report',
  standalone: true,
  imports: [CommonModule, FormsModule, DateFormatPipe, PhoneFormatPipe, MaxLengthDirective],
  templateUrl: './team-report.component.html',
  styleUrls: ['./team-report.component.scss']
})
export class TeamReportComponent implements OnInit {
  private teamReportService = inject(TeamReportService);
  private orgContextService = inject(OrganizationContextService);

  // Loading and error states
  isLoading = false;
  hasError = false;
  errorMessage = '';

  // Export loading states
  isExportingCsv = false;
  isExportingPdf = false;

  // Data from API
  teamMembers: TeamMember[] = [];
  filteredMembers: TeamMember[] = [];
  tests: SafetyTestInfo[] = [];
  summary: SummaryCounts = {
    total: 0,
    current: 0,
    expiringSoon: 0,
    expired: 0,
    incomplete: 0
  };

  // Search and filter
  searchQuery = '';
  statusFilter: 'all' | 'current' | 'expiring' | 'expired' | 'incomplete' = 'all';

  // Expanded member details
  expandedMemberId: string | null = null;

  ngOnInit(): void {
    this.loadTeamReport();
  }

  /**
   * Load team report data from the API
   */
  loadTeamReport(): void {
    const orgId = this.orgContextService.getCurrentOrganizationId();

    if (!orgId) {
      this.hasError = true;
      this.errorMessage = 'No organization selected. Please select an organization to view the team report.';
      return;
    }

    this.isLoading = true;
    this.hasError = false;
    this.errorMessage = '';

    this.teamReportService.getTeamReport(orgId).subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.teamMembers = response.data.members;
          this.tests = response.data.tests;
          this.summary = response.data.summary;
          this.filterMembers();
        } else {
          this.hasError = true;
          this.errorMessage = response.error || 'Failed to load team report data.';
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.hasError = true;
        this.errorMessage = 'Unable to load team report. Please try again.';
        this.isLoading = false;
        console.error('Error loading team report:', err);
      }
    });
  }

  /**
   * Retry loading the team report after an error
   */
  retryLoad(): void {
    this.loadTeamReport();
  }

  /**
   * Filter members based on search query and status filter
   */
  filterMembers(): void {
    let filtered = [...this.teamMembers];

    // Apply status filter
    if (this.statusFilter !== 'all') {
      filtered = filtered.filter(m => this.matchesStatusFilter(m));
    }

    // Apply search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(query) ||
        m.email.toLowerCase().includes(query) ||
        m.role.toLowerCase().includes(query)
      );
    }

    this.filteredMembers = filtered;
  }

  /**
   * Check if a member matches the current status filter.
   * Handles special case for 'expiring' filter which checks test results.
   */
  private matchesStatusFilter(member: TeamMember): boolean {
    switch (this.statusFilter) {
      case 'expiring':
        // "Expiring" filter matches members who have at least one test with 'expiring_soon' status
        return member.testResults.some(t => t.status === 'expiring_soon');

      case 'current':
        // Current members with no expiring tests
        return member.overallStatus === 'current' &&
               !member.testResults.some(t => t.status === 'expiring_soon');

      case 'expired':
        return member.overallStatus === 'expired';

      case 'incomplete':
        return member.overallStatus === 'in_progress' || member.overallStatus === 'not_started';

      default:
        return true;
    }
  }

  onSearchChange(): void {
    this.filterMembers();
  }

  onStatusFilterChange(status: 'all' | 'current' | 'expiring' | 'expired' | 'incomplete'): void {
    this.statusFilter = status;
    this.filterMembers();
  }

  toggleMemberDetails(memberId: string): void {
    if (this.expandedMemberId === memberId) {
      this.expandedMemberId = null;
    } else {
      this.expandedMemberId = memberId;
    }
  }

  isMemberExpanded(memberId: string): boolean {
    return this.expandedMemberId === memberId;
  }

  // Summary card counts from API response
  get currentCount(): number {
    return this.summary.current;
  }

  get expiringCount(): number {
    return this.summary.expiringSoon;
  }

  get expiredCount(): number {
    return this.summary.expired;
  }

  get incompleteCount(): number {
    return this.summary.incomplete;
  }

  // Empty state computed properties
  /**
   * Check if there are no team members in the organization
   */
  get hasNoTeamMembers(): boolean {
    return this.teamMembers.length === 0 && !this.isLoading && !this.hasError;
  }

  /**
   * Check if there are no safety tests configured for the organization
   */
  get hasNoTestsConfigured(): boolean {
    return this.tests.length === 0 && this.teamMembers.length > 0 && !this.isLoading && !this.hasError;
  }

  /**
   * Check if the current filter/search returns no results
   * (but there are team members available)
   */
  get hasFilteredEmptyState(): boolean {
    return this.filteredMembers.length === 0 &&
           this.teamMembers.length > 0 &&
           this.tests.length > 0 &&
           !this.isLoading &&
           !this.hasError;
  }

  /**
   * Check if filters are currently active
   */
  get hasActiveFilters(): boolean {
    return this.searchQuery.trim().length > 0 || this.statusFilter !== 'all';
  }

  /**
   * Clear all filters and reset to default view
   */
  clearFilters(): void {
    this.searchQuery = '';
    this.statusFilter = 'all';
    this.filterMembers();
  }

  // Delegate helper methods to service
  getMemberPassedCount(member: TeamMember): number {
    return this.teamReportService.getMemberPassedCount(member);
  }

  getMemberTotalTests(member: TeamMember): number {
    return member.testResults.length;
  }

  getCompletionPercentage(member: TeamMember): number {
    return this.teamReportService.getCompletionPercentage(member);
  }

  getDaysUntilExpiration(dateString: string | null): number {
    return this.teamReportService.getDaysUntilExpiration(dateString);
  }

  isExpiringSoon(dateString: string | null): boolean {
    return this.teamReportService.isExpiringSoon(dateString);
  }

  isExpired(dateString: string | null): boolean {
    if (!dateString) return false;
    return this.getDaysUntilExpiration(dateString) <= 0;
  }

  getCertStatusClass(status: MemberOverallStatus): string {
    return this.teamReportService.getMemberStatusClass(status);
  }

  getCertStatusText(status: MemberOverallStatus): string {
    return this.teamReportService.getStatusText(status);
  }

  getTestStatusClass(status: TestResultStatus): string {
    return this.teamReportService.getTestStatusClass(status);
  }

  getTestStatusIcon(status: TestResultStatus): string {
    return this.teamReportService.getTestStatusIcon(status);
  }

  getTestStatusText(status: TestResultStatus): string {
    return this.teamReportService.getStatusText(status);
  }

  /**
   * Get display name for a test (shortName or truncated title)
   */
  getTestDisplayName(test: TestResult): string {
    return test.shortName || test.testName.substring(0, 10);
  }

  viewMemberDetails(member: TeamMember): void {
    console.log('View member details:', member.id);
  }

  sendReminder(member: TeamMember): void {
    console.log('Send reminder to:', member.id);
  }

  /**
   * Legacy export method (kept for backward compatibility)
   */
  exportReport(): void {
    this.exportCsv();
  }

  /**
   * Export team report as CSV
   */
  exportCsv(): void {
    const orgId = this.orgContextService.getCurrentOrganizationId();
    if (!orgId) {
      console.error('No organization selected for export');
      return;
    }

    this.isExportingCsv = true;

    this.teamReportService.exportCsv(orgId).subscribe({
      next: (blob) => {
        const filename = this.generateFilename('csv');
        this.teamReportService.downloadFile(blob, filename);
        this.isExportingCsv = false;
      },
      error: (err) => {
        console.error('Error exporting CSV:', err);
        this.isExportingCsv = false;
      }
    });
  }

  /**
   * Export team report as PDF (Summary format)
   */
  exportPdfSummary(): void {
    this.exportPdf('summary');
  }

  /**
   * Export team report as PDF (Detailed format)
   */
  exportPdfDetailed(): void {
    this.exportPdf('detailed');
  }

  /**
   * Export team report as PDF with specified type
   */
  private exportPdf(type: 'summary' | 'detailed'): void {
    const orgId = this.orgContextService.getCurrentOrganizationId();
    if (!orgId) {
      console.error('No organization selected for export');
      return;
    }

    this.isExportingPdf = true;

    this.teamReportService.exportPdf(orgId, type).subscribe({
      next: (blob) => {
        const filename = this.generateFilename('pdf', type);
        this.teamReportService.downloadFile(blob, filename);
        this.isExportingPdf = false;
      },
      error: (err) => {
        console.error('Error exporting PDF:', err);
        this.isExportingPdf = false;
      }
    });
  }

  /**
   * Generate filename for export
   * Format: team-report-{YYYY-MM-DD}.{extension} or team-report-{type}-{YYYY-MM-DD}.{extension}
   */
  private generateFilename(extension: 'csv' | 'pdf', type?: 'summary' | 'detailed'): string {
    const date = new Date().toISOString().split('T')[0];
    if (type) {
      return `team-report-${type}-${date}.${extension}`;
    }
    return `team-report-${date}.${extension}`;
  }

  /**
   * Check if any export is in progress
   */
  get isExporting(): boolean {
    return this.isExportingCsv || this.isExportingPdf;
  }
}
